import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { StickyContainer } from 'react-sticky';

import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { ISmevMonitoringIndex } from 'APIPackage/SmevMonitoringModule/interfaces/ISmevMonitoringIndex';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { Table } from 'CorePackage/TableModule/components/Table';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IconMenu } from 'UIPackage/IconsModule/components/IconMenu';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';

import styles from './SmevMonitoring.module.scss';
import columnStyles from './SmevMonitoringTable.module.scss';
import {
  smevMonitoringDownloadMessage,
  smevMonitoringGetMessageById,
  smevMonitoringGetTableData,
  smevMonitoringGetTableDataNextPage,
  smevMonitoringSetOrder,
  smevMonitoringInitPage,
} from '../actions/smevMonitoringActions';
import { getSmevMonitoringTableData, getSmevMonitoringTotalCount } from '../selectors/smevMonitoringSelectors';
import { initialSmevMonitoringTableColumns } from '../constants/initialSmevMonitoringTableColumns';
import { SmevMonitoringCharts } from './SmevMonitoringCharts';
import { SmevMonitoringFilters } from './SmevMonitoringFilters';
import { SmevMonitoringAnswerChainModal } from './SmevMonitoringAnswerChainModal';
import { SmevMonitoringColumnTypeEnum } from '../enums/SmevMonitoringColumnType';
import { SmevMonitoringStatusesEnum } from '../enums/SmevMonitoringStatusesEnum';

const rowClassNames = (row: ISmevMonitoringIndex) => {
  const { processingStatus, sendingStatus } = row;

  return {
    ...(sendingStatus === SmevMonitoringStatusesEnum.NEW && { 'data-blue': true }),
    ...((sendingStatus === SmevMonitoringStatusesEnum.SENT ||
      sendingStatus === SmevMonitoringStatusesEnum.SUCCESS ||
      sendingStatus === SmevMonitoringStatusesEnum.PROCESSED) && { 'data-green': true }),
    ...((sendingStatus === SmevMonitoringStatusesEnum.FAILED ||
      sendingStatus === SmevMonitoringStatusesEnum.FAULT ||
      processingStatus === SmevMonitoringStatusesEnum.FAILED) && { 'data-red': true }),
    ...(sendingStatus === SmevMonitoringStatusesEnum.QUEUED && { 'data-grey': true }),
    ...(sendingStatus === SmevMonitoringStatusesEnum.REJECTED && { 'data-yellow': true }),
  };
};

interface ISmevMonitoringProps {
  data: ISmevMonitoringIndex[];
  totalCount: number;
}

const SmevMonitoringWithoutStore = ({ data, totalCount }: ISmevMonitoringProps) => {
  const [tableColumns, setTableColumns] = useState<ITableColumn<ISmevMonitoringIndex>[]>(
    initialSmevMonitoringTableColumns,
  );

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(smevMonitoringInitPage());
  }, [dispatch]);

  useEffect(() => {
    const order = getSortOrder(initialSmevMonitoringTableColumns);

    dispatch(smevMonitoringSetOrder(order));
  }, [dispatch]);

  const handleSortClick = useCallback(
    (id: keyof ISmevMonitoringIndex) => {
      const newColumnsData = handleSortChange(tableColumns, id);

      setTableColumns(newColumnsData);
      const newOrder = getSortOrder(newColumnsData);

      dispatch(smevMonitoringSetOrder(newOrder));
      dispatch(smevMonitoringGetTableData());
    },
    [dispatch, tableColumns],
  );

  const handleLoadClick = useCallback(() => dispatch(smevMonitoringGetTableDataNextPage()), [dispatch]);

  const handleDownloadMessage = useCallback(
    (id: number, type: SmevMonitoringColumnTypeEnum) => () => {
      dispatch(smevMonitoringDownloadMessage(id, type));
    },
    [dispatch],
  );

  const renderDropdown = useCallback(
    (row: ISmevMonitoringIndex) => {
      const dropdownItems: IDropdownItem[] = [];

      if (row.receiveContent) {
        dropdownItems.push({
          id: 'download-xml-receive',
          content: (
            <DropdownItem onClick={handleDownloadMessage(row.id, SmevMonitoringColumnTypeEnum.RECEIVE)}>
              Скачать xml входящего сообщения
            </DropdownItem>
          ),
        });
      }
      if (row.sendContent) {
        dropdownItems.push({
          id: 'download-xml-send',
          content: (
            <DropdownItem onClick={handleDownloadMessage(row.id, SmevMonitoringColumnTypeEnum.SEND)}>
              Скачать xml исходящего сообщения
            </DropdownItem>
          ),
        });
      }
      if (row.smevContent) {
        dropdownItems.push({
          id: 'download-xml-smev',
          content: (
            <DropdownItem onClick={handleDownloadMessage(row.id, SmevMonitoringColumnTypeEnum.SMEV)}>
              Скачать xml статусного сообщения от СМЭВ
            </DropdownItem>
          ),
        });
      }

      return (
        <Dropdown items={dropdownItems}>
          <button className={columnStyles.MenuButton} type="button">
            <IconMenu className={columnStyles.MenuButtonIcon} />
          </button>
        </Dropdown>
      );
    },
    [handleDownloadMessage],
  );

  const handleRowDoubleClick = useCallback(
    (row: ISmevMonitoringIndex) => (e: MouseEvent<HTMLTableRowElement>) => {
      if (!(e.target instanceof HTMLInputElement)) {
        dispatch(smevMonitoringGetMessageById(row.id));
      }
    },
    [dispatch],
  );

  return (
    <>
      <div className={styles.MainContent}>
        <SmevMonitoringCharts />
        <SmevMonitoringFilters />
        <StickyContainer>
          <Table<ISmevMonitoringIndex>
            data={data}
            columns={tableColumns}
            columnStyles={columnStyles}
            onSortChange={handleSortClick}
            totalCount={totalCount}
            handleLoadClick={handleLoadClick}
            renderDropdown={renderDropdown}
            rowClassNames={rowClassNames}
            onRowDoubleClick={handleRowDoubleClick}
          />
        </StickyContainer>
        <SmevMonitoringAnswerChainModal />
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  data: getSmevMonitoringTableData(state),
  totalCount: getSmevMonitoringTotalCount(state),
});

export const SmevMonitoring = connect(mapStateToProps)(SmevMonitoringWithoutStore);
